import {
  Button,
  Card,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Step,
  StepButton,
  Stepper,
  Typography
} from '@mui/material';
import {
  PHASE_DATES,
  PHASES_LABEL_MAP,
  STRATEGY_PHASE_DATES
} from '../../shared/AppConstants';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  clearModalMessages,
  fetchSavePhaseDates,
  fetchSaveStrategy,
  setModalSuccessMessage
} from './phasesSlice';
import { PhaseModalMessageBanner } from './PhaseModalMessageBanner';
import { PhaseModalContent } from './PhaseModalContent';

const hasCompletionDate = (phaseData) => {
  if (phaseData?.actualCompletionDate) {
    return true;
  }
  return false;
};

const getPhaseDate = (phaseData) => {
  if (phaseData?.actualCompletionDate) {
    return phaseData?.actualCompletionDate;
  } else if (phaseData?.estimatedCompletionDate) {
    return `Target: ${phaseData?.estimatedCompletionDate}`;
  } else {
    return 'N/A';
  }
};

export const PhaseBanner = () => {
  const [showModal, setShowModal] = useState(false);
  const [savePhaseDatesSuccess, setSavePhaseDatesSuccess] = useState(false);
  const [saveStrategySuccess, setSaveStrategySuccess] = useState(false);

  const dispatch = useDispatch();
  const activePhase = useSelector((state) => state.phases.activePhase);
  const phaseDataById = useSelector((state) => state.phases.phaseData ?? {});

  const handleOpenModal = () => {
    dispatch(clearModalMessages());
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSaveStrategySuccess(false);
    setSavePhaseDatesSuccess(false);
  };

  const handleSave = () => {
    dispatch(fetchSavePhaseDates()).then((response) => {
      if (response.type === 'phases/fetchSavePhaseDates/fulfilled') {
        setSavePhaseDatesSuccess(true);
      }
    });

    dispatch(fetchSaveStrategy()).then((response) => {
      if (response.type === 'phases/fetchSaveStrategy/fulfilled') {
        setSaveStrategySuccess(true);
      }
    });
  };

  useEffect(() => {
    if (savePhaseDatesSuccess && saveStrategySuccess) {
      dispatch(setModalSuccessMessage());
    }
  }, [dispatch, savePhaseDatesSuccess, saveStrategySuccess]);

  return (
    <Card sx={(theme) => ({ mt: theme.fuel6Tokens.JddcSpacingMedium })}>
      <Grid container>
        <Grid size={10.5}>
          <Stepper
            activeStep={Math.max((activePhase ?? 1) - 1, 0)}
            sx={{
              p: '0.5rem',
              '& .MuiStepLabel-iconContainer': {
                display: 'none'
              }
            }}
          >
            {Object.entries(PHASES_LABEL_MAP).map(([key, value], index) => {
              const phaseId = Number(key);
              const phaseLabel = value;
              const phaseData = phaseDataById[phaseId];

              return (
                <Step
                  key={phaseId}
                  completed={phaseId < activePhase}
                  sx={(theme) => ({
                    borderRadius: theme.fuel6Tokens.JddcRadiusSmall,
                    background:
                      phaseId === activePhase
                        ? theme.fuel6Tokens.JddcColorBackgroundSuccessSubtle
                        : theme.fuel6Tokens.JddcColorBackgroundPrimary,
                    margin: theme.fuel6Tokens.JddcSpacingSmall,
                    padding: theme.fuel6Tokens.JddcSpacingSmall
                  })}
                >
                  <StepButton
                    aria-controls="stepper-content"
                    optional={
                      <Typography variant="bodyMedium">
                        <strong>{phaseLabel} </strong>
                        <br />
                        {`${getPhaseDate(phaseData)}`}
                      </Typography>
                    }
                  >
                    {`PHASE ${phaseId}`}
                    {(phaseId >= activePhase ||
                      hasCompletionDate(phaseData)) && (
                      <Chip
                        variant={'badge'}
                        label={
                          phaseId < activePhase && hasCompletionDate(phaseData)
                            ? 'COMPLETE'
                            : phaseId === activePhase
                              ? 'CURRENT'
                              : 'NOT STARTED'
                        }
                        color={
                          phaseId < activePhase
                            ? 'success'
                            : phaseId === activePhase
                              ? 'informational'
                              : 'neutral'
                        }
                        sx={(theme) => ({
                          ml: theme.fuel6Tokens.JddcSpacingSmall
                        })}
                      ></Chip>
                    )}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
        </Grid>
        <Grid size={1.5}>
          <Grid
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Button
              variant={'tertiary'}
              startIcon={<fuel-icon name="edit" />}
              onClick={handleOpenModal}
            >
              {PHASE_DATES}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        aria-describedby={'manage-phases-modal-description'}
        aria-labelledby={'manage-phases-modal-title'}
        open={showModal}
        onClose={handleCloseModal}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'min(700px, calc(100vw - 48px))'
          }
        }}
      >
        <DialogTitle id={'manage-phases-modal-title'}>
          <CardHeader
            action={
              <IconButton aria-label="close" onClick={handleCloseModal}>
                <fuel-icon name={'close'} />
              </IconButton>
            }
            title={STRATEGY_PHASE_DATES}
          />
        </DialogTitle>
        <DialogContent id={'manage-phases-modal-description'}>
          <PhaseModalMessageBanner />
          <PhaseModalContent />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary" onClick={handleCloseModal} fullWidth>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={handleSave}
            fullWidth
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
